import { routerAbi } from '@/configs/abis/router';
import { QUERY_KEYS } from '@/constants/query';
import { queryClient } from '@/global';
import { useChainId, useDappChainConfig, useUserAddr } from '@/hooks/web3/useChain';
import { useWalletSigner } from '@/hooks/web3/useWalletNetwork';
import { TxSubmitRequest } from '@/types/agent';
import { TaskState } from '@/types/gelato';
import { pollTaskStatus } from '@/utils/gelato';
import { saveAgentJWTToken } from '@/utils/storage';
import { AxiosError } from 'axios';
import { Contract, ethers } from 'ethers';
import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { usePublicClient, useWalletClient } from 'wagmi';
import { enableOrDisableGelatoAgent, sendTransactionFromServer, txLogin } from './api';
import { useAgentStore } from './store';

export function useUserAgentSetting(userAddr: string | undefined) {
  const { userAgentSettings } = useAgentStore();
  const agentSettings = useMemo(() => (userAddr ? userAgentSettings[userAddr] : null), [userAddr, userAgentSettings]);
  const { agentAddress, jwtToken, isEnableAgentMode } = useMemo(
    () => ({
      agentAddress: agentSettings?.agentAddress,
      jwtToken: agentSettings?.jwtToken,
      isEnableAgentMode: agentSettings?.isEnableAgentMode,
    }),
    [agentSettings],
  );

  return {
    agentSettings,
    agentAddress,
    jwtToken,
    isEnableAgentMode,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cachedNotify: { [tx: string]: any } = {};

export function useSignMessageForGelatoJWT() {
  // const { signMessage, isPending, data:signature, error } = useSignMessage();
  const userAddr = useUserAddr();
  const { data: client } = useWalletClient();
  const chainId = useChainId();

  const signMsg = useCallback(async () => {
    if (!userAddr || !client || !chainId) return;
    const timestamp = new Date().getTime();
    const nonce = new Date().getTime();

    const agreement = `Generate a SynFutures 1CT (One-Click Trading) session. Only sign this message on a trusted website.`;
    const message = `Chain Id: ${chainId}
Wallet Address: ${userAddr}
Nonce: ${nonce}
${agreement}`;

    const signature = await client?.signMessage({
      message: message,
    });

    return {
      nonce,
      signature,
      message,
      timestamp,
    };
  }, [chainId, client, userAddr]);

  return { signMsg };
}

/**
 * Create a signer message for Frontend TX JWT authentication.
 * Ensures message format and account selection are compatible with backend validation.
 */
export function useSignMessageForTxJWT() {
  // const { signMessage, isPending, data:signature, error } = useSignMessage();
  const { data: client } = useWalletClient();
  const chainId = useChainId();

  const signMsg = useCallback(async () => {
    if (!client || !chainId) return;

    // const testD = await testLogin(client, chainId);
    // console.log('🚀 ~ useAgentAction ~ testD:', testD);

    const nonce = Math.floor(Date.now() / 1000);

    const message = `Chain ID: ${chainId}\nWallet Address: ${client.account.address}\nNonce: ${nonce}\nSign this message to authenticate with SynFutures`;

    const signature = await client?.signMessage({
      message: message,
      account: client.account,
    });

    return {
      nonce,
      signature,
      message,
    };
  }, [chainId, client]);

  return { signMsg };
}

export function useAgentAction() {
  const { setAgentAddress, setJWTToken, setIsTradingModeChanging, isTradingModeChanging } = useAgentStore();

  const chainId = useChainId();
  const dappConfig = useDappChainConfig(chainId);
  const userAddr = useUserAddr();
  const signer = useWalletSigner();
  const { signMsg } = useSignMessageForTxJWT();

  // Get current user's agent settings
  const { agentAddress, jwtToken, isEnableAgentMode } = useUserAgentSetting(userAddr);
  // const { mutate: login } = useLoginMutation();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId });

  /**
   * Sign login message and request a Frontend TX JWT token.
   * Persists token and proxy address into local store upon success.
   */
  const signAndLogJWTToken = useCallback(async () => {
    if (!userAddr || !chainId) return;
    if (!walletClient) return;

    const data = await signMsg();
    if (!data) return;
    const { nonce, signature } = data;
    const resp = await txLogin({
      chainId,
      nonce,
      signature,
      userAddr: walletClient.account.address,
    });

    if (resp?.data?.data?.token) {
      saveAgentJWTToken(userAddr, resp?.data?.data?.token);
      setJWTToken(userAddr, resp?.data?.data?.token);
      setAgentAddress(userAddr, resp?.data?.data?.proxyAddr);
    }

    return resp;
  }, [userAddr, chainId, walletClient, signMsg, setJWTToken, setAgentAddress]);

  const signAgentData = useCallback(
    async (enableAgent: boolean, agentAddress: string) => {
      try {
        if (!agentAddress || !userAddr || !signer) return;
        const routerAddress = dappConfig?.contracts?.router;
        if (!routerAddress) return;
        const routerContract = new Contract(routerAddress, routerAbi, signer);
        if (!routerContract) return;

        const deadline = Math.floor(Date.now() / 1000) + 3600; // 1 hour ago

        const nonce = await routerContract.getNonce(userAddr);

        // Create EIP-712 signature
        const domain = {
          name: 'GelatoRelayRouter',
          version: '1',
          chainId: chainId,
          verifyingContract: routerAddress,
        };

        const types = {
          SubAccountManagement: [
            { name: 'action', type: 'uint8' },
            { name: 'subAccount', type: 'address' },
            { name: 'nonce', type: 'uint256' },
            { name: 'deadline', type: 'uint256' },
          ],
        };

        const value = {
          action: enableAgent ? 0 : 1,
          subAccount: agentAddress,
          nonce: nonce,
          deadline: deadline,
        };

        const signature = await signer._signTypedData(domain, types, value);

        return { signature, deadline };
      } catch (error) {
        console.log('🚀 ~ useAgentAction ~ error:', error);
        return;
      }
    },
    [userAddr, signer, dappConfig?.contracts?.router, chainId],
  );

  const signAndPostAgent = useCallback(
    async (
      enableAgent: boolean,
      agentAddr: string,
      jwt?: string,
      hooks?: {
        onStart?: () => void;
      },
    ) => {
      // client && (await main(client));
      if (!chainId || !userAddr) return;
      if (!jwt) {
        jwt = jwtToken?.token;
      }
      const signatureRes = await signAgentData(enableAgent, agentAddr);

      if (signatureRes) {
        hooks?.onStart && hooks?.onStart?.();
        const { signature, deadline } = signatureRes;

        if (dappConfig?._1CTTxRequestType === 'gelato') {
          // send signature to enable agent
          const resp = await enableOrDisableGelatoAgent(
            {
              chainId: chainId,
              action: enableAgent ? 0 : 1,
              deadline: deadline,
              signature: signature,
            },
            jwt,
          );

          if (resp?.data?.data?.taskId) {
            const taskId = resp.data.data.taskId;
            const status = await pollTaskStatus(taskId);

            return { status, isExecSuccess: status.taskState === TaskState.ExecSuccess };
          }
        } else {
          const sig = ethers.utils.splitSignature(signature);

          const routerAddress = dappConfig?.contracts?.router;
          if (!routerAddress) return;
          const routerContract = new Contract(routerAddress, routerAbi, signer);

          if (!routerContract) return;

          const callData1 = routerContract.interface.encodeFunctionData('executeSubAccountManagement', [
            userAddr,
            enableAgent ? 0 : 1, // SET_SUBACCOUNT
            agentAddr,
            deadline,
            sig.v,
            sig.r,
            sig.s,
          ]);

          const submitData: TxSubmitRequest = {
            chainID: chainId,
            calldata: callData1,
            to: routerAddress,
            userAddr: userAddr,
          };

          // send signature to enable agent
          const resp = await sendTransactionFromServer(
            submitData,

            jwt,
          );

          const txHash = resp?.data?.data?.txHash;

          if (txHash && publicClient) {
            const receipt = await publicClient.waitForTransactionReceipt({
              hash: txHash,
            });
            return {
              isExecSuccess: receipt.status === 'success',
              receipt,
            };
          }
        }
      }
    },
    [chainId, dappConfig?.contracts?.router, jwtToken?.token, publicClient, signAgentData, signer, userAddr],
  );

  const tryEnableAgent = useCallback(async () => {
    if (!chainId) return;

    try {
      let agentAddr = agentAddress;
      let jwt = jwtToken?.token;
      if (!jwt || !agentAddress) {
        const resp = await signAndLogJWTToken();
        if (!resp?.data?.data?.proxyAddr) return;
        agentAddr = resp?.data?.data?.proxyAddr;
        jwt = resp?.data?.data?.token;
      }
      if (!agentAddr) return;
      // if agent is already enabled, no need to sign and post
      if (agentAddress && isEnableAgentMode) {
        return;
      }
      const taskStatus = await signAndPostAgent(true, agentAddr, jwt, {
        onStart: () => {
          setIsTradingModeChanging(true);
          const id = toast.loading(`One-Click Mode enabling...`);
          if (userAddr) cachedNotify[userAddr] = id;
        },
      });

      if (taskStatus) {
        if (taskStatus?.isExecSuccess) {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AGENT.AGENT_ADDRESS_FROM_CHAIN(chainId, userAddr) });
          // setIsEnableAgentMode(true);
          // setTradingMode(TradingMode.Agent);
          setIsTradingModeChanging(false);
          if (userAddr) {
            toast.update(cachedNotify[userAddr], {
              type: 'success',
              render: `One-Click Mode enabled successfully`,
              isLoading: false,
              closeButton: true,
              closeOnClick: true,
              autoClose: 5000,
            });
            delete cachedNotify[userAddr];
          }
          return true;
        } else {
          if (userAddr) {
            toast.update(cachedNotify[userAddr], {
              type: 'error',
              render: `Failed to enable agent`,
              isLoading: false,
              closeButton: true,
              closeOnClick: true,
              autoClose: 5000,
            });
            delete cachedNotify[userAddr];
          }
        }
      }
      setIsTradingModeChanging(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      let errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || error.response?.data?.msg || errorMessage;
      }
      if (userAddr) {
        toast.update(cachedNotify[userAddr], {
          type: 'error',
          render: `Failed to enable One-Click Mode, error msg:${errorMessage}`,
          isLoading: false,
          closeButton: true,
          closeOnClick: true,
          autoClose: 5000,
        });
        delete cachedNotify[userAddr];
      }
      setIsTradingModeChanging(false);
      console.error('🚀 ~ useAgentAction ~ error:', error);
      return false;
    }
  }, [
    chainId,
    setIsTradingModeChanging,
    agentAddress,
    jwtToken?.token,
    isEnableAgentMode,
    signAndPostAgent,
    signAndLogJWTToken,
    userAddr,
  ]);
  const tryDisableAgent = useCallback(async () => {
    try {
      let agentAddr = agentAddress;
      let jwt = jwtToken?.token;
      if (!jwt || !agentAddress) {
        const resp = await signAndLogJWTToken();
        if (!resp?.data?.data?.proxyAddr) return;
        agentAddr = resp?.data?.data?.proxyAddr;
        jwt = resp?.data?.data?.token;
      }
      if (!agentAddr || !isEnableAgentMode) return;
      const taskStatus = await signAndPostAgent(false, agentAddr, jwt, {
        onStart: () => {
          setIsTradingModeChanging(true);
          const id = toast.loading(`One-Click Mode disabling...`);
          if (userAddr) cachedNotify[userAddr] = id;
        },
      });

      if (taskStatus) {
        if (taskStatus?.isExecSuccess) {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AGENT.AGENT_ADDRESS_FROM_CHAIN(chainId, userAddr) });
          // setIsEnableAgentMode(false);
          // setTradingMode(TradingMode.Classic);
          if (userAddr) {
            toast.update(cachedNotify[userAddr], {
              type: 'success',
              render: `One-Click Mode disabled successfully`,
              isLoading: false,
              closeButton: true,
              closeOnClick: true,
              autoClose: 5000,
            });
            delete cachedNotify[userAddr];
          }
          setIsTradingModeChanging(false);
          return true;
        } else {
          if (userAddr) {
            toast.update(cachedNotify[userAddr], {
              type: 'error',
              render: `Failed to disable One-Click Mode`,
              isLoading: false,
              closeButton: true,
              closeOnClick: true,
              autoClose: 5000,
            });
            delete cachedNotify[userAddr];
          }
        }
      }
      setIsTradingModeChanging(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      let errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      if (error instanceof AxiosError) {
        errorMessage = error.response?.data?.message || error.response?.data?.msg || errorMessage;
      }
      setIsTradingModeChanging(false);
      if (userAddr) {
        toast.update(cachedNotify[userAddr], {
          type: 'error',
          render: `Failed to disable One-Click Mode, error msg:${errorMessage}`,
          isLoading: false,
          closeButton: true,
          closeOnClick: true,
          autoClose: 5000,
        });
        delete cachedNotify[userAddr];
      }
      console.error('🚀 ~ useAgentAction ~ error:', error);
      return false;
    }
  }, [
    setIsTradingModeChanging,
    agentAddress,
    jwtToken?.token,
    isEnableAgentMode,
    signAndPostAgent,
    signAndLogJWTToken,
    chainId,
    userAddr,
  ]);

  return {
    setAgentAddress,
    tryEnableAgent,
    tryDisableAgent,
    isTradingModeChanging,
  };
}

export function useIsEnableAgent() {
  const { userAgentSettings } = useAgentStore();
  const userAddr = useUserAddr();

  return useMemo(
    // () => Boolean(true),
    () => {
      if (!userAddr) return false;
      const agentSettings = userAgentSettings[userAddr];
      return Boolean(agentSettings?.jwtToken?.token && agentSettings?.agentAddress && agentSettings?.isEnableAgentMode);
    },
    [userAgentSettings, userAddr],
  );
}
